import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import CommissioningContent from "@/components/services/CommissioningContent";
import FacilityQualificationContent from "@/components/services/FacilityQualificationContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";

import { qualValOfferingData } from "@/data/qualValOfferingData";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getQVOfferingMeta, getLocalizedQVOfferingData, getQVOfferingUI } from "@/i18n/qualValOfferings";
import { getServiceContent } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";

import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

interface FaqItem {
  q: string;
  a: string;
}

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  contentComponent: string;
  ogImage?: string;
  faqItems?: FaqItem[];
  contentTags?: string[];
}> = {
  commissioning: {
    title: "Commissioning",
    subtitle: "Tailored commissioning services for optimal performance and safety",
    description: "Commissioning is crucial for getting your systems and equipment to perform at their best and be safe across industries. At QbD Group we are experts in making sure your systems, utilities, facilities and equipment are up and running just right.",
    contentComponent: "commissioning",
    contentTags: ["Qualification & Validation"],
  },
  "facility-qualification": {
    title: "Facility Qualification",
    subtitle: "Validate your facilities for reliability, efficiency, and compliance",
    description: "At QbD Group, we offer facility qualification services tailored to your specific industry and regulatory requirements. By partnering with us, you can trust that your facilities and utilities are validated to the highest standards, ensuring the reliability, efficiency, and safety of your operations.",
    contentComponent: "facility-qualification",
    contentTags: ["Qualification & Validation"],
  },
  "utility-qualification": {
    title: "Utility Qualification",
    subtitle: "Validate your utilities for compliance, reliability, and efficiency",
    description: "At QbD Group, we offer utility qualification services tailored to your specific industry and regulatory requirements. Partner with QbD Group to ensure your utilities are validated to the highest standards, guaranteeing reliability, efficiency, and safety in your operations.",
    contentComponent: "utility-qualification",
    contentTags: ["Qualification & Validation"],
  },
  "equipment-qualification": {
    title: "Equipment Qualification",
    subtitle: "Flexible and customized solutions for equipment qualification",
    description: "Are you looking for a turnkey solution for the C&Q and Equipment Qualification part of your project? Whether you are building or renovating and need to qualify your equipment, we are here to help! From simple to more complex equipment and systems, QbD Group offers flexible and customized solutions to meet your needs.",
    contentComponent: "equipment-qualification",
    contentTags: ["Qualification & Validation"],
  },
  "process-validation": {
    title: "Process Validation",
    subtitle: "Ensure product quality and compliance with validated processes",
    description: "Are you looking for expertise in process validation? Process Validation is a critical component of maintaining product quality, regulatory compliance, and operational excellence in manufacturing. By partnering with QbD Group, you can trust that your processes are validated to the highest standards.",
    contentComponent: "process-validation",
    contentTags: ["Qualification & Validation", "Pharma & Biotech"],
  },
  "cleaning-validation": {
    title: "Cleaning Validation",
    subtitle: "Ensure safe and compliant manufacturing with validated cleaning processes",
    description: "QbD offers a wide range of qualification and validation services for your processes, applications, and equipment. Our experts have in-depth knowledge of cleaning validation, ensuring your operations meet the highest standards.",
    contentComponent: "cleaning-validation",
    contentTags: ["Qualification & Validation", "GMP"],
  },
  "analytical-method-validation": {
    title: "Analytical Method Validation",
    subtitle: "Validate your analytical methods for accuracy, compliance, and reliability",
    description: "Ensure your pharmaceutical products meet the highest regulatory standards with our comprehensive analytical method validation services. We specialize in providing precise, reliable, and compliant solutions to help you validate analytical methods critical for quality control.",
    contentComponent: "analytical-method-validation",
    contentTags: ["Qualification & Validation", "Lab Services"],
  },
  "sterilization-validation": {
    title: "Sterilization Validation",
    subtitle: "Ensure product safety and compliance with validated sterilization processes",
    description: "We specialize in comprehensive sterilization validation services tailored to your specific industry and regulatory requirements. Sterilization Validation is a critical component of ensuring product safety, regulatory compliance, and consumer confidence.",
    contentComponent: "sterilization-validation",
    contentTags: ["Qualification & Validation"],
  },
  "tech-transfer": {
    title: "Technology Transfer",
    subtitle: "Streamline your technology transfer process for efficiency and compliance",
    description: "Technology transfer is a vital process in translating innovation into real-world applications, driving economic growth, and fostering collaboration and innovation. Our experienced QbD Group team offers comprehensive support, from technology evaluation and documentation to process optimization and training.",
    contentComponent: "tech-transfer",
    contentTags: ["Qualification & Validation", "Pharma & Biotech"],
  },
  "process-product-support": {
    title: "Process & Product Support",
    subtitle: "Optimize processes and enhance product quality with expert support",
    description: "Our experienced team offers expertise in process optimization, quality management, regulatory compliance, and continuous improvement methodologies to help you achieve operational and product excellence.",
    contentComponent: "process-product-support",
    contentTags: ["Qualification & Validation"],
  },
};

/** Map Spanish slugs → English content keys */
const ES_SLUG_MAP: Record<string, string> = {
  "cualificacion-de-instalaciones": "facility-qualification",
  "cualificacion-de-servicios": "utility-qualification",
  "cualificacion-de-equipos": "equipment-qualification",
  "validacion-de-procesos": "process-validation",
  "validacion-de-limpieza": "cleaning-validation",
  "validacion-de-metodos-analiticos": "analytical-method-validation",
  "validacion-de-esterilizacion": "sterilization-validation",
  "soporte-de-procesos-y-productos": "process-product-support",
};

/** Extract the offering slug from any pathname variant and resolve to content key */
const getOfferingSlug = (pathname: string): string | null => {
  const match = pathname.match(/\/(?:services|servicios)\/qualification-validation\/([^/]+)$/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  return lookupSlug(ES_SLUG_MAP, raw);
};

const QualValOffering = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const ui = getQVOfferingUI(lang);
  const servicesPath = localizeHref("/en/services", lang);
  const qualificationValidationPath = localizeHref("/en/services/qualification-validation", lang);

  const slug = getOfferingSlug(location.pathname);
  const page = slug ? offeringPages[slug] : undefined;
  const parentService = getServiceBySlug("qualification-validation");

  if (!page || !parentService) return <Navigate to={qualificationValidationPath} replace />;

  // Get translated parent service content
  const parentI18n = getServiceContent("qualification-validation", lang);
  const displayParent = parentI18n
    ? { ...parentService, formHeading: parentI18n.formHeading, formSubtitle: parentI18n.formSubtitle }
    : parentService;

  // Get translated offering metadata
  const i18nMeta = getQVOfferingMeta(page.contentComponent, lang);
  const displayTitle = i18nMeta?.title ?? page.title;
  const displaySubtitle = i18nMeta?.subtitle ?? page.subtitle;
  const displayDescription = i18nMeta?.description ?? page.description;
  const displayFaqItems = i18nMeta?.faqItems ?? page.faqItems;

  const heroService = {
    ...parentService,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const metaTitle = `${displayTitle} — ${ui.qualVal} | QbD Group`;
  const metaDesc = displayDescription.slice(0, 155);
  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Qualification & Validation"];

  const renderContent = () => {
    const ctaClick = () => setFormOpen(true);
    switch (page.contentComponent) {
      case "commissioning":
        return <CommissioningContent onCtaClick={ctaClick} lang={lang} />;
      case "facility-qualification":
        return <FacilityQualificationContent onCtaClick={ctaClick} />;
      default: {
        const rawData = qualValOfferingData[page.contentComponent];
        if (rawData) {
          const data = getLocalizedQVOfferingData(page.contentComponent, rawData, lang);
          return <GenericOfferingContent data={data} onCtaClick={ctaClick} hidePartnerSection />;
        }
        return null;
      }
    }
  };

  // Get localized partner override
  const getPartnerOverride = () => {
    const rawData = qualValOfferingData[page.contentComponent];
    if (!rawData) return undefined;
    const data = getLocalizedQVOfferingData(page.contentComponent, rawData, lang);
    return { intro: data.partnerIntro, bullets: data.partnerBullets };
  };

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        canonical={pageUrl}
        ogImage={pageOgImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: displayTitle,
            description: displayDescription,
            provider: { "@type": "Organization", name: "QbD Group", url: SITE },
            url: pageUrl,
            isPartOf: { "@type": "Service", name: parentService.title, url: `${SITE}${parentService.path}` },
            ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)),
          })}
        </script>
        {displayFaqItems && displayFaqItems.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: displayFaqItems.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              })),
            })}
          </script>
        )}
      </SEOHead>

      
      <BreadcrumbJsonLd
        items={[
          { name: lang === "es" ? "Servicios" : lang === "zh-cn" ? "服务" : "Services", path: servicesPath },
          { name: ui.qualVal, path: qualificationValidationPath },
          { name: displayTitle },
        ]}
      />

      <ScrollToTop />
      <Navbar logoOverride={DIVISION_LOGOS["qualification-validation"]} />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        <ServiceHero
          service={heroService}
          onCtaClick={() => setFormOpen(true)}
          hideDiscoverButton
          parentCrumbs={[
            { label: ui.qualVal, to: qualificationValidationPath },
          ]}
        />

        {renderContent()}

        <ServiceWhyQbd
          service={parentService}
          onCtaClick={() => setFormOpen(true)}
          partnerOverride={getPartnerOverride()}
        />

        {displayFaqItems && displayFaqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "GxP validation requirements", es: "Requisitos de validaci\u00f3n GxP", "zh-cn": "GxP验证实施要求" }[lang])}</p>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">{ui.faqTitle}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {displayFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        <ServiceRelatedContent service={parentService} contentTags={relatedTags} />

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{displayParent.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={parentService.title}
              hubspotFormId={parentService.hubspotFormId}
              heading={displayParent.formHeading}
              subtitle={displayParent.formSubtitle}
            />
          </DialogContent>
        </Dialog>

        <ContactCTA
          onContactClick={() => setFormOpen(true)}
          title={displayParent.formHeading}
          description={displayParent.formSubtitle}
        />
      </main>

      <Footer />
    </>
  );
};

export default QualValOffering;
